/**
 * Act3TopBar — 第三幕顶部栏
 *
 * 显示周数、当前考验、三维指标
 */
import React from 'react';
import { Settings, Heart, Zap, Shield } from 'lucide-react';
import { ACT3_MAX_WEEKS } from '@shared/constants/gameBalance';

interface Act3TopBarProps {
  week: number;
  trialName: string;
  trialCategory: string;
  commitmentA: number;
  commitmentB: number;
  energyA: number;
  energyB: number;
  stability: number;
  nameA: string;
  nameB: string;
  onSettingsClick?: () => void;
}

export const Act3TopBar: React.FC<Act3TopBarProps> = React.memo(({
  week,
  trialName,
  trialCategory,
  commitmentA,
  commitmentB,
  energyA,
  energyB,
  stability,
  nameA,
  nameB,
  onSettingsClick,
}) => {
  return (
    <div className="bg-white/80 backdrop-blur-md border-b border-[#E5E0D8] flex flex-col z-10 shrink-0">
      {/* Week + trial info */}
      <div className="flex items-center justify-center pt-1.5 pb-0.5 relative gap-2">
        <span className="text-[10px] font-medium text-[#9CA3AF]">
          第三幕 · 第 {week}/{ACT3_MAX_WEEKS} 周
        </span>
        <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium bg-red-100 text-red-700">
          {trialCategory} · {trialName}
        </span>
        {onSettingsClick && (
          <button
            onClick={onSettingsClick}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full hover:bg-[#F5EDE3] transition-colors text-[#C4B9AC] hover:text-[#6B7280]"
            aria-label="设置"
          >
            <Settings size={14} />
          </button>
        )}
      </div>

      {/* Metrics */}
      <div className="px-3 pb-2 space-y-1">
        <PersonRow name={nameA} commitment={commitmentA} energy={energyA} />
        <PersonRow name={nameB} commitment={commitmentB} energy={energyB} />
        <StabilityBar stability={stability} />
      </div>
    </div>
  );
});

Act3TopBar.displayName = 'Act3TopBar';

const PersonRow: React.FC<{ name: string; commitment: number; energy: number }> = ({ name, commitment, energy }) => (
  <div className="flex items-center gap-1.5">
    <span className="text-[9px] text-coral-500 font-medium w-10 shrink-0 truncate">{name}</span>
    <MetricBar icon={<Heart size={8} />} value={commitment} max={100} color="bg-red-400" />
    <MetricBar icon={<Zap size={8} />} value={energy} max={100} color="bg-amber-400" />
  </div>
);

const StabilityBar: React.FC<{ stability: number }> = ({ stability }) => (
  <div className="flex items-center gap-1.5">
    <span className="text-[9px] text-purple-500 font-medium w-10 shrink-0">稳定性</span>
    <MetricBar icon={<Shield size={8} />} value={stability} max={100} color="bg-blue-400" />
  </div>
);

const MetricBar: React.FC<{ icon: React.ReactNode; value: number; max: number; color: string }> = ({ icon, value, max, color }) => {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <div className="flex items-center gap-0.5 flex-1">
      <span className="text-[#9CA3AF]">{icon}</span>
      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-500 ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[8px] text-[#9CA3AF] w-5 text-right">{value}</span>
    </div>
  );
};
